import React, { useContext, useEffect, useState } from "react"
import trcss from "../../../styles/Telerecovery.module.css"
import { formatToLocalDateTime, formatChannels, formatDuration, getDeviceTypeIcon, GET_STAFF_NAMES, findName } from "../Utils"
import { getMeetingProviderText } from "../Types/TypeUtils"
import { MeetingProvider } from "../Types/Enums"
import { Message } from 'primereact/message'
import ExternalLinkPopup from "../ExternalLinkPopup"
import { useQuery } from "@apollo/client"
import { UserContext } from "../../../context/userContext"
import { ProgressSpinner } from 'primereact/progressspinner'

export default function MeetingHistory({ meetingHistory, last }) {

    const [showExternalLinkPopup, setShowExternalLinkPopup] = useState(false)
    const userCtx = useContext(UserContext)
    const { user } = userCtx.session
    const [meetingHistoryWithNames, setMeetingHistoryWithNames] = useState(meetingHistory)
    const [loadingNames, setLoadingNames] = useState(false)

    const { refetch } = useQuery(GET_STAFF_NAMES, {
        variables: {
            staff_id: user.staff_id,
            organization_id: user.organization_id,
            staff_ids: [user.staff_id]
        },
        skip: true,
    })

    useEffect(() => {
        const getStaffsNames = async (staffIds) => {
            if (staffIds.length > 0) {
                setLoadingNames(true)
                const res = await refetch({
                    staff_id: user.staff_id,
                    organization_id: user.organization_id,
                    staff_ids: [...new Set(staffIds)]
                })
                setLoadingNames(false)
                if (res.data.getStaffNamesByIds?.data) {
                    const staffNames = (JSON.parse(res.data.getStaffNamesByIds.data))
                    const _meetingHistory = meetingHistory.map((mr) => {
                        mr["staffName"] = findName(mr.staffId, staffNames)
                        return mr
                    })
                    setMeetingHistoryWithNames(_meetingHistory)
                }
            } else {
                setMeetingHistoryWithNames(meetingHistory)
            }
        }
        if (meetingHistory) {
            const staffIds = meetingHistory.map(mr => mr.staffId)
            getStaffsNames(staffIds)
        }
    }, [meetingHistory])

    const renderExtraInfo = (meetingRoom) => {
        if (meetingRoom.extraInfo && meetingRoom.extraInfo.durationInSec > 0) {
            return (
                <div className={trcss.extra_info}>
                    <hr style={{ margin: "5px 0" }} />
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div className={trcss.field} style={{ width: "auto" }}>
                            <label className={trcss.label}>Duration</label>
                            <p>{formatDuration(meetingRoom.extraInfo.durationInSec)}</p>
                        </div>
                        {meetingRoom.extraInfo.totalParticipants &&
                            <div className={trcss.field} style={{ width: "auto" }}>
                                <label className={trcss.label}>Attendees #</label>
                                <p>{meetingRoom.extraInfo.totalParticipants}</p>
                            </div>
                        }
                    </div>
                    {meetingRoom.extraInfo.participantsInfo.length > 0 &&
                        <div>
                            <b>Attendees</b>
                            <ul>
                                {meetingRoom.extraInfo.participantsInfo.map((pi, pIndex) =>
                                    <li key={pIndex}>
                                        <div style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "10px",
                                            alignItems: "center"
                                        }}>
                                            <i className={`${getDeviceTypeIcon(pi.device_type)} device_type`} style={{ fontSize: "12px" }} />
                                            <div>
                                                <p>{pi.name}</p>
                                                <p>{formatDuration(pi.duration_seconds)}</p>
                                            </div>
                                        </div>
                                    </li>
                                )
                                }
                            </ul>
                        </div>
                    }
                </div>
            )
        }
        if (meetingRoom.provider === MeetingProvider["Google Meet"]) {
            return (
                <div className={trcss.extra_info}>
                    <hr style={{ margin: "5px 0" }} />
                    <Message
                        severity="info"
                        text="This information will be available soon"
                        className={trcss.info_message_white}
                    />
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                        <div className={trcss.field} style={{ width: "auto" }}>
                            <label className={trcss.label}>Duration</label>
                            <p>--:--:--</p>
                        </div>
                    </div>
                    <div>
                        <b>Attendees</b>
                        <ul>
                            <li>
                                <div style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "10px",
                                    alignItems: "center"
                                }}>
                                    <i className={`${getDeviceTypeIcon(null)} device_type`} style={{ fontSize: "12px" }} />
                                    <div>
                                        <p>Participant 1</p>
                                        <p>--:--:--</p>
                                    </div>
                                </div>
                            </li>
                            <li>
                                <div style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: "10px",
                                    alignItems: "center"
                                }}>
                                    <i className={`${getDeviceTypeIcon(null)} device_type`} style={{ fontSize: "12px" }} />
                                    <div>
                                        <p>Participant 2</p>
                                        <p>--:--:--</p>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            )
        }
        return null
    }

    if (!meetingHistory || meetingHistory.length === 0) {
        return null
    }

    return (
        <div className={trcss.meeting_history_wrapper}>
            <h2>Meeting History <span>{`(Last ${last})`}</span></h2>
            <div className={trcss.meeting_history}>
                {meetingHistoryWithNames.map((meetingRoom, index) => {
                    return (
                        <div className={trcss.meeting_history_element} key={index}>
                            <div className={trcss.field}>
                                <label className={trcss.label}>Provider</label>
                                <p>{getMeetingProviderText(meetingRoom.provider)}</p>
                            </div>
                            <div className={trcss.field}>
                                <label className={trcss.label}>Date</label>
                                <p>{formatToLocalDateTime(meetingRoom.sessionStartedAt)}</p>
                            </div>
                            <div className={trcss.field}>
                                <label className={trcss.label}>Link</label>
                                <ExternalLinkPopup
                                    link={meetingRoom.joinLink}
                                    showPopup={showExternalLinkPopup}
                                    setShowPopup={setShowExternalLinkPopup}
                                />
                                <p className={trcss.link} onClick={(e) => { setShowExternalLinkPopup(true) }}>
                                    {meetingRoom.joinLink}
                                </p>
                            </div>
                            <hr style={{ margin: "5px 0" }} />
                            <div className={trcss.field}>
                                <label className={trcss.label}>Staff name</label>
                                {loadingNames ?
                                    <ProgressSpinner
                                        style={{ width: "18px", height: "18px" }}
                                        strokeWidth="4"
                                        animationDuration=".5s"
                                    /> :
                                    <p>{meetingRoom.staffName?.full_name ?? ""}</p>
                                }
                            </div>
                            <div className={trcss.field}>
                                <label className={trcss.label}>Invite message</label>
                                <p>{meetingRoom.inviteMessage}</p>
                            </div>
                            <div className={trcss.field}>
                                <label className={trcss.label}>Message channels</label>
                                <p>{formatChannels(meetingRoom.channels)}</p>
                            </div>
                            {renderExtraInfo(meetingRoom)}
                        </div>
                    )
                })
                }
            </div>
        </div>
    )
}